/**
 * Admin Marketing — Plays, Goals, Series API.
 * `/api/admin/v2/marketing/{plays,goals,series}`
 *
 * Progress and pacing on the scorecard are computed **server-side** and consumed
 * as given. Recomputing them in the browser would mean two implementations of the
 * same arithmetic, and the one that drifts is always the one someone is reading.
 */
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import useGetQueryFn from '../use-get-query-fn';
import useGetMutationFn from '../use-get-mutation-fn';
import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from '@/client/constants';
import type { AdminEnvelope } from '@/client/types/admin/users-orgs';
import type {
  ApiGoal,
  ApiPlay,
  ApiPlayScorecard,
  ApiPlaysOverview,
  ApiSeries,
  MarketingList,
  MarketingListParams,
} from '@/client/types/marketing/api';
import { MARKETING_BASE, marketingKeys } from './keys';

/* ------------------------------------------------------------------ plays */

export const usePlaysQuery = (params: MarketingListParams = {}) => {
  const queryFn = useGetQueryFn<AdminEnvelope<MarketingList<ApiPlay>>>({
    defaultErrorMessage: 'Could not load plays',
    endpoint: `${MARKETING_BASE}/plays`,
  });
  return useQuery({
    queryKey: marketingKeys.plays.list(params),
    queryFn: () => queryFn({ ...params }),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
    select: (response) => response?.data,
  });
};

export const usePlayQuery = (playId: number, enabled = true) => {
  const queryFn = useGetQueryFn<AdminEnvelope<ApiPlay>>({
    defaultErrorMessage: 'Could not load the play',
    endpoint: `${MARKETING_BASE}/plays/${playId}`,
  });
  return useQuery({
    queryKey: marketingKeys.plays.detail(playId),
    queryFn: () => queryFn(),
    enabled: enabled && Number.isFinite(playId),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    select: (response) => response?.data,
  });
};

/**
 * Play plus its goals, each carrying progress, pacing and a health verdict.
 *
 * Pacing is time-dependent, so this is refetched more eagerly than the play
 * itself — a scorecard that says "on track" from an hour-old computation is the
 * one thing on the page that must not be stale.
 */
export const usePlayScorecardQuery = (playId: number, enabled = true) => {
  const queryFn = useGetQueryFn<AdminEnvelope<ApiPlayScorecard>>({
    defaultErrorMessage: 'Could not load the scorecard',
    endpoint: `${MARKETING_BASE}/plays/${playId}/scorecard`,
  });
  return useQuery({
    queryKey: marketingKeys.plays.scorecard(playId),
    queryFn: () => queryFn(),
    enabled: enabled && Number.isFinite(playId),
    staleTime: 1000 * 60,
    gcTime: DEFAULT_GC_TIME,
    select: (response) => response?.data,
  });
};

/**
 * The whole Plays page in one call.
 *
 * Every play with a count of what is attached to it, every goal already carrying
 * progress/pacing/health, and what is linked to no play at all. The alternative
 * — loading content, series, outreach, posts and placements into the browser to
 * count them, and scoring goals here — is a second implementation of the pacing
 * arithmetic, and the copy that drifts is always the one someone is reading.
 *
 * Same 60s staleness as the scorecard: health is time-dependent, so it moves
 * with nobody on the page.
 */
export const usePlaysOverviewQuery = () => {
  const queryFn = useGetQueryFn<AdminEnvelope<ApiPlaysOverview>>({
    defaultErrorMessage: 'Could not load plays',
    endpoint: `${MARKETING_BASE}/plays/overview`,
  });
  return useQuery({
    queryKey: marketingKeys.plays.overview(),
    queryFn: () => queryFn(),
    staleTime: 1000 * 60,
    gcTime: DEFAULT_GC_TIME,
    select: (response) => response?.data,
  });
};

export const useCreatePlayMutation = () => {
  const queryClient = useQueryClient();
  const mutationFn = useGetMutationFn<Partial<ApiPlay>, AdminEnvelope<ApiPlay>>({
    defaultErrorMessage: 'Could not create the play',
    endpoint: `${MARKETING_BASE}/plays`,
    method: 'POST',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: marketingKeys.plays.all }),
  });
};

export const useUpdatePlayMutation = () => {
  const queryClient = useQueryClient();
  const mutationFn = useGetMutationFn<
    Partial<ApiPlay> & { play_id: number },
    AdminEnvelope<ApiPlay>
  >({
    defaultErrorMessage: 'Could not update the play',
    endpoint: (payload) => `${MARKETING_BASE}/plays/${payload.play_id}`,
    method: 'PATCH',
  });
  return useMutation({
    mutationFn,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: marketingKeys.plays.all });
      // The scorecard embeds the play, so a status or budget change has to
      // invalidate it too or the header and the goals disagree.
      queryClient.invalidateQueries({
        queryKey: marketingKeys.plays.scorecard(variables.play_id),
      });
    },
  });
};

export const useDeletePlayMutation = () => {
  const queryClient = useQueryClient();
  const mutationFn = useGetMutationFn<{ play_id: number }, AdminEnvelope<unknown>>({
    defaultErrorMessage: 'Could not remove the play',
    endpoint: (payload) => `${MARKETING_BASE}/plays/${payload.play_id}`,
    method: 'DELETE',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: marketingKeys.plays.all });
      // Goals survive a deleted play (their play_id is nulled, not cascaded), so
      // the goals list changes shape and must be refetched.
      queryClient.invalidateQueries({ queryKey: marketingKeys.goals.all });
    },
  });
};

/* ------------------------------------------------------------------ goals */

export const useGoalsQuery = (params: MarketingListParams = {}) => {
  const queryFn = useGetQueryFn<AdminEnvelope<MarketingList<ApiGoal>>>({
    defaultErrorMessage: 'Could not load goals',
    endpoint: `${MARKETING_BASE}/goals`,
  });
  return useQuery({
    queryKey: marketingKeys.goals.list(params),
    queryFn: () => queryFn({ ...params }),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
    select: (response) => response?.data,
  });
};

export const useCreateGoalMutation = () => {
  const queryClient = useQueryClient();
  const mutationFn = useGetMutationFn<Partial<ApiGoal>, AdminEnvelope<ApiGoal>>({
    defaultErrorMessage: 'Could not create the goal',
    endpoint: `${MARKETING_BASE}/goals`,
    method: 'POST',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: marketingKeys.goals.all });
      queryClient.invalidateQueries({ queryKey: marketingKeys.plays.all });
    },
  });
};

export const useUpdateGoalMutation = () => {
  const queryClient = useQueryClient();
  const mutationFn = useGetMutationFn<
    Partial<ApiGoal> & { goal_id: number },
    AdminEnvelope<ApiGoal>
  >({
    defaultErrorMessage: 'Could not update the goal',
    endpoint: (payload) => `${MARKETING_BASE}/goals/${payload.goal_id}`,
    method: 'PATCH',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: marketingKeys.goals.all });
      queryClient.invalidateQueries({ queryKey: marketingKeys.plays.all });
    },
  });
};

export const useDeleteGoalMutation = () => {
  const queryClient = useQueryClient();
  const mutationFn = useGetMutationFn<{ goal_id: number }, AdminEnvelope<unknown>>({
    defaultErrorMessage: 'Could not remove the goal',
    endpoint: (payload) => `${MARKETING_BASE}/goals/${payload.goal_id}`,
    method: 'DELETE',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: marketingKeys.goals.all });
      queryClient.invalidateQueries({ queryKey: marketingKeys.plays.all });
    },
  });
};

/* ----------------------------------------------------------------- series */

export const useSeriesQuery = (params: MarketingListParams = {}) => {
  const queryFn = useGetQueryFn<AdminEnvelope<MarketingList<ApiSeries>>>({
    defaultErrorMessage: 'Could not load series',
    endpoint: `${MARKETING_BASE}/series`,
  });
  return useQuery({
    queryKey: marketingKeys.series.list(params),
    queryFn: () => queryFn({ ...params }),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
    select: (response) => response?.data,
  });
};

/** Editions due soon — what the series runner will pick up next. */
export const useSeriesDueQuery = (withinDays = 14) => {
  const queryFn = useGetQueryFn<AdminEnvelope<{ count: number; items: ApiSeries[] }>>({
    defaultErrorMessage: 'Could not load due series',
    endpoint: `${MARKETING_BASE}/series/due`,
  });
  return useQuery({
    queryKey: marketingKeys.series.due(withinDays),
    queryFn: () => queryFn({ within_days: withinDays }),
    staleTime: 1000 * 60,
    gcTime: DEFAULT_GC_TIME,
    select: (response) => response?.data,
  });
};

export const useCreateSeriesMutation = () => {
  const queryClient = useQueryClient();
  const mutationFn = useGetMutationFn<Partial<ApiSeries>, AdminEnvelope<ApiSeries>>({
    defaultErrorMessage: 'Could not create the series',
    endpoint: `${MARKETING_BASE}/series`,
    method: 'POST',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: marketingKeys.series.all });
      // The plays overview counts series per play, so it goes stale too.
      queryClient.invalidateQueries({ queryKey: marketingKeys.plays.all });
    },
  });
};

export const useUpdateSeriesMutation = () => {
  const queryClient = useQueryClient();
  const mutationFn = useGetMutationFn<
    Partial<ApiSeries> & { series_id: number },
    AdminEnvelope<ApiSeries>
  >({
    defaultErrorMessage: 'Could not update the series',
    endpoint: (payload) => `${MARKETING_BASE}/series/${payload.series_id}`,
    method: 'PATCH',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: marketingKeys.series.all });
      // The plays overview counts series per play, so it goes stale too.
      queryClient.invalidateQueries({ queryKey: marketingKeys.plays.all });
    },
  });
};

export const useDeleteSeriesMutation = () => {
  const queryClient = useQueryClient();
  const mutationFn = useGetMutationFn<{ series_id: number }, AdminEnvelope<unknown>>({
    defaultErrorMessage: 'Could not remove the series',
    endpoint: (payload) => `${MARKETING_BASE}/series/${payload.series_id}`,
    method: 'DELETE',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: marketingKeys.series.all });
      // The plays overview counts series per play, so it goes stale too.
      queryClient.invalidateQueries({ queryKey: marketingKeys.plays.all });
    },
  });
};
